import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Person } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Modal from '../../../component/Modal';
import SocketManager from '../../../services/SocketManager';
import languages from '../../../constants/languages';
import './DocumentTranslationLoaderWithProgress.css';

const DocumentTranslationLoaderWithProgress = ({
	loading,
	sourceLanguage,
	targetLanguage,
	wordCount,
	onClose,
	requestId,
	userId,
}) => {
	const [showProfileHint, setShowProfileHint] = useState(false);
	const [percentage, setPercentage] = useState(0);
	const [statusMessage, setStatusMessage] = useState(
		'Initializing translation service...'
	);
	const navigate = useNavigate();
	const hasReceivedFirstEvent = useRef(false);

	const mainMessage =
		'Your translation may take anywhere from a few minutes to several hours as we are utilizing a Large Language Model (LLM) to ensure context-aware translation. Each paragraph is translated individually to maintain the quality and accuracy of the text.';

	const secondaryMessage =
		'You can close this message and check the progress in your translation history by clicking your profile icon in the top right corner. Thank you for your understanding and patience!';

	useEffect(() => {
		if (loading && requestId && userId) {
			setPercentage(0);
			setStatusMessage('Initializing translation service...');
			hasReceivedFirstEvent.current = false;

			SocketManager.emit('join_translation_room', userId);

			const handleTranslationStarted = (data) => {
				if (data?.requestId === requestId) {
					setStatusMessage('Translating document content...');
					hasReceivedFirstEvent.current = true;
				}
			};

			const handleTranslationProgress = (data) => {
				if (data?.requestId === requestId) {
					if (!hasReceivedFirstEvent.current) {
						hasReceivedFirstEvent.current = true;
						setStatusMessage('Analyzing document structure...');
					}
					setPercentage(data.percentage || 0);

					if (data.percentage > 0 && data.percentage < 10) {
						setStatusMessage('Analyzing document structure...');
					} else if (data.percentage >= 10 && data.percentage < 90) {
						setStatusMessage('Translating document content...');
					} else if (data.percentage >= 90 && data.percentage < 100) {
						setStatusMessage('Finalizing translation...');
					}
				}
			};

			const handleTranslationFinished = (data) => {
				if (data?.requestId === requestId) {
					setPercentage(data.percentage || 90);
					setStatusMessage('Generating translated file...');
				}
			};

			const handleTranslationReady = (data) => {
				if (data?.requestId === requestId) {
					setPercentage(100);
					setStatusMessage('Translation completed successfully');
				}
			};

			const handleTranslationProcessReverted = (data) => {
				if (data?.requestId === requestId) {
					setStatusMessage('Switching to alternative translation method...');
					setPercentage(data.percentage || 0);
				}
			};

			SocketManager.on('translation-started', handleTranslationStarted);
			SocketManager.on('translation-progress', handleTranslationProgress);
			SocketManager.on('translation-finished', handleTranslationFinished);
			SocketManager.on('translation-ready', handleTranslationReady);
			SocketManager.on(
				'translation-process-reverted',
				handleTranslationProcessReverted
			);

			return () => {
				SocketManager.off('translation-started', handleTranslationStarted);
				SocketManager.off('translation-progress', handleTranslationProgress);
				SocketManager.off('translation-finished', handleTranslationFinished);
				SocketManager.off('translation-ready', handleTranslationReady);
				SocketManager.off(
					'translation-process-reverted',
					handleTranslationProcessReverted
				);
			};
		}
	}, [loading, requestId, userId]);

	useEffect(() => {
		if (loading) {
			document.body.style.overflow = 'hidden';

			const hintTimer = setTimeout(() => {
				setShowProfileHint(true);
			}, 2000);

			return () => {
				document.body.style.overflow = 'unset';
				clearTimeout(hintTimer);
			};
		} else {
			setShowProfileHint(false);
			setPercentage(0);
			document.body.style.overflow = 'unset';
		}
	}, [loading]);

	const handleProfileClick = () => {
		navigate('/document-translation-history');
	};

	const handleClose = () => {
		onClose();
	};

	useEffect(() => {
		return () => {
			document.body.style.overflow = 'unset';
		};
	}, []);

	const getLanguageName = (code) => {
		if (!code || code === 'auto') return 'Auto Detect';
		const lang = languages.find((l) => l.code === code);
		return lang ? lang.name : code;
	};

	return (
		<Modal
			open={loading}
			handleClose={handleClose}
			title="Document Translation in Progress"
			className="document-translation-modal"
		>
			<div className="document-loader-content">
				<div className="loader-wrapper">
					<div className="circular-loader"></div>
					<div className="percentage">{Math.floor(percentage)}%</div>
				</div>

				<div className="translation-info">
					<Typography variant="h6" className="loader-subtitle">
						{`${getLanguageName(sourceLanguage)} → ${getLanguageName(
							targetLanguage
						)}`}
					</Typography>

					{wordCount && (
						<Typography variant="body2" className="word-count">
							{wordCount} words
						</Typography>
					)}
				</div>

				<div className="progress-message">
					<Typography variant="body1" className="message-text">
						{statusMessage}
					</Typography>
				</div>

				<div className="progress-message">
					<Typography variant="body2" className="secondary-message">
						{mainMessage}
					</Typography>
				</div>

				<Typography variant="body2" className="secondary-message">
					{secondaryMessage}
				</Typography>

				{showProfileHint && (
					<div className="profile-hint">
						<Box className="hint-content">
							<Person className="hint-icon" />
							<div className="hint-text">
								<Typography variant="body2" className="hint-title">
									Want to check your translation history?
								</Typography>
								<Typography variant="caption" className="hint-subtitle">
									Click on your profile icon in the top right corner, then
									select "Document Translation History" from the dropdown menu
								</Typography>
							</div>
						</Box>
						<Button
							variant="outlined"
							size="small"
							onClick={handleProfileClick}
							className="hint-button"
						>
							View History
						</Button>
					</div>
				)}
			</div>
		</Modal>
	);
};

export default DocumentTranslationLoaderWithProgress;
